import { cmsQueryParamsPrefix } from '../../utils/routing';
import {
  optionKeyOf,
  type FilterCategory,
  type FilterOption,
  type FilterSelection,
} from './productFilterTypes';

/**
 * One parameter per filter group, composed from the prefix that owns it — the
 * same convention as `cms_sort` and the PL prefill params (CMS-1903 R10.4).
 * A group's own `categoryId` names it, so adding a group needs no code change.
 */
export const filterQueryParam = (categoryId: string): string =>
  `${cmsQueryParamsPrefix}${categoryId}`;

/**
 * Multiple values go in one comma-separated parameter rather than a repeated
 * key: the PL prefill convention these sit beside uses single-valued params,
 * and one key per group keeps a shared URL readable.
 *
 * A value containing a comma cannot survive this, which is why the values are
 * uids rather than labels — a label is localized and may contain anything.
 */
const SEPARATOR = ',';

export const encodeFilterValues = (values: readonly string[]): string =>
  values.join(SEPARATOR);

export const decodeFilterValues = (raw: string | null): string[] =>
  raw
    ? raw
        .split(SEPARATOR)
        .map((value) => value.trim())
        .filter(Boolean)
    : [];

/**
 * The row key a URL token names, or `undefined` when the group offers none.
 *
 * Any exact match beats any case-insensitive one, and within each a row key
 * beats an alias: exact key, exact alias, key ignoring case, alias ignoring
 * case. The alias steps are how a link written before a group existed still
 * selects it (CMS-2087); case is ignored because cms enforces a lowercase
 * slug for a group key while the member keys it replaces were typed in whatever
 * case the editor chose. Earlier rows win a tie.
 */
const makeTokenResolver = (options: readonly FilterOption[]) => {
  const keys = new Set<string>();
  const aliases = new Map<string, string>();
  const lowerKeys = new Map<string, string>();
  const lowerAliases = new Map<string, string>();

  const addOnce = (map: Map<string, string>, token: string, key: string) => {
    if (!map.has(token)) map.set(token, key);
  };

  options.forEach((option) => {
    const key = optionKeyOf(option);
    keys.add(key);
    addOnce(lowerKeys, key.toLowerCase(), key);
    (option.aliases ?? []).forEach((alias) => {
      addOnce(aliases, alias, key);
      addOnce(lowerAliases, alias.toLowerCase(), key);
    });
  });

  return (token: string): string | undefined => {
    const lower = token.toLowerCase();
    return (
      (keys.has(token) ? token : undefined) ??
      aliases.get(token) ??
      lowerKeys.get(lower) ??
      lowerAliases.get(lower)
    );
  };
};

/**
 * The selection a URL describes, narrowed to what the resolved groups actually
 * offer.
 *
 * An unrecognised value is dropped rather than kept (R10.3): a group left
 * holding only unknown values would match no product, so the page would render
 * empty with no way for the reader to see why. Dropping restores that group to
 * its no-filter state, which is what the requirement asks for.
 */
export const readFilterSelection = (
  categories: readonly FilterCategory[],
  read: (param: string) => string | null,
): FilterSelection => {
  const selection: Record<string, readonly string[]> = {};

  categories.forEach((category) => {
    const { categoryId } = category;
    if (!categoryId) return;

    // The URL carries an option's key, which is what identifies it — the same
    // thing `applyProductFilter` matches a selection against. Keyed on `value`
    // here, every band's key read as an unknown option and was dropped, so a
    // shared link silently lost its filters.
    // Tokens resolve to row keys first, so the selection never holds an alias.
    const resolve = makeTokenResolver(category.options);
    const chosen = decodeFilterValues(read(filterQueryParam(categoryId)))
      .flatMap((token) => {
        const key = resolve(token);
        return key === undefined ? [] : [key];
      })
      // A repeated value in the URL must not count twice against an AND group,
      // and two aliases of one group are one selection.
      .filter((value, index, all) => all.indexOf(value) === index);

    if (chosen.length > 0) selection[categoryId] = chosen;
  });

  return selection;
};

/** Every parameter the panel owns, so Clear All can drop them in one write. */
export const filterQueryParams = (
  categories: readonly FilterCategory[],
): string[] =>
  categories.flatMap((category) =>
    category.categoryId ? [filterQueryParam(category.categoryId)] : [],
  );
